import React from "react";
import { formatTimestamp } from "@/utils/timezone";
import { decodeHtmlEntities, formatCurrency } from "@/utils/formatters";
import "./StockTwitsCard.css";

/**
 * Convert URLs and ticker symbols in text to clickable links or inline images
 */
const linkifyText = (text, onTickerClick) => {
  // Match URLs and ticker symbols ($TICK)
  const regex = /(https?:\/\/[^\s]+)|(\$[A-Z]{1,5})/g;
  const parts = text.split(regex).filter(Boolean);

  return parts.map((part, index) => {
    // URL
    if (part.match(/^https?:\/\//)) {
      // Check if URL is an image
      const imageExtensions = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i;
      if (part.match(imageExtensions)) {
        return (
          <div key={index} style={{ margin: "0.5rem 0" }}>
            <img
              src={part}
              alt="Embedded content"
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
      }
      // Regular link
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ color: "#3182ce", textDecoration: "underline" }}
        >
          {part}
        </a>
      );
    }
    // Ticker symbol
    if (part.match(/^\$/)) {
      const ticker = part.substring(1); // Remove $
      return (
        <span
          key={index}
          onClick={(e) => {
            e.stopPropagation();
            if (onTickerClick) {
              onTickerClick(ticker);
            }
          }}
          style={{
            color: "#3182ce",
            fontWeight: "600",
            cursor: "pointer",
            textDecoration: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = "underline";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = "none";
          }}
        >
          {part}
        </span>
      );
    }
    return part;
  });
};

/**
 * StockTwitsCard - Renders a StockTwits post from API data
 * Designed to match the official StockTwits UI
 */
const StockTwitsCard = ({ intel, onTickerClick, onAuthorClick, onImageClick }) => {
  if (!intel.raw_data) {
    return null;
  }

  const data = intel.raw_data;
  const user = data.user || {};
  const symbols = data.symbols || [];
  const entities = data.entities || {};
  const sentiment = entities.sentiment;
  const chart = entities.chart;

  // Get the actual reshared message (it's nested inside .message)
  const reshareMessage =
    data.reshare_message?.message ||
    data.reshared_message?.message ||
    data.reshares?.message;

  // Parse likes count
  const likesCount = data.likes?.total || 0;

  // Extract and decode HTML entities from body
  const body = decodeHtmlEntities(data.body || "");

  return (
    <div className="stocktwits-card">
      {/* Header: User info */}
      <div className="stocktwits-card-header">
        <img
          src={user.avatar_url || "/default-avatar.png"}
          alt={user.username}
          className="stocktwits-avatar"
          onClick={(e) => {
            e.stopPropagation();
            if (onAuthorClick) {
              onAuthorClick(user.username);
            }
          }}
          style={{ cursor: "pointer" }}
          title={`Filter by @${user.username}`}
        />
        <div className="stocktwits-user-info">
          <div
            className="stocktwits-username"
            onClick={(e) => {
              e.stopPropagation();
              if (onAuthorClick) {
                onAuthorClick(user.username);
              }
            }}
            style={{ cursor: "pointer" }}
            title={`Filter by @${user.username}`}
          >
            {user.username}
            {user.official && (
              <span className="stocktwits-verified-badge" title="Verified">
                ✓
              </span>
            )}
          </div>
          <div className="stocktwits-timestamp">
            {formatTimestamp(data.created_at, "MMM dd, yyyy h:mm a")}
          </div>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="stocktwits-card-content">
        {/* Post body */}
        <div className="stocktwits-body">
          {body.split("\n").map((line, i) => (
            <p key={i}>{linkifyText(line, onTickerClick)}</p>
          ))}
        </div>

        {/* Chart image (if available) - shown before reshare */}
        {chart &&
          (chart.default || chart.original || chart.large || chart.thumb) && (
            <div
              className="stocktwits-chart"
              onClick={(e) => {
                if (onImageClick) {
                  e.stopPropagation();
                  onImageClick(
                    chart.original || chart.large || chart.default || chart.thumb
                  );
                }
              }}
              style={{ cursor: onImageClick ? "pointer" : "default" }}
              title={onImageClick ? "Click to view full size" : undefined}
            >
              <img
                src={
                  chart.default || chart.original || chart.large || chart.thumb
                }
                alt="Chart"
              />
            </div>
          )}

        {/* Reshared post (if this is a reshare) */}
        {reshareMessage && (
          <div className="stocktwits-reshare">
            <div className="stocktwits-reshare-header">
              <img
                src={reshareMessage.user?.avatar_url || "/default-avatar.png"}
                alt={reshareMessage.user?.username}
                className="stocktwits-reshare-avatar"
              />
              <div className="stocktwits-reshare-user">
                <span className="stocktwits-reshare-username">
                  {reshareMessage.user?.username}
                </span>
                <span className="stocktwits-reshare-timestamp">
                  {formatTimestamp(reshareMessage.created_at, "MMM dd, yyyy")}
                </span>
              </div>
            </div>
            <div className="stocktwits-reshare-body">
              {decodeHtmlEntities(reshareMessage.body || "")
                .split("\n")
                .map((line, i) => (
                  <p key={i}>{linkifyText(line, onTickerClick)}</p>
                ))}
            </div>
            {/* Reshared post chart (if available) */}
            {reshareMessage.entities?.chart &&
              (reshareMessage.entities.chart.default ||
                reshareMessage.entities.chart.original ||
                reshareMessage.entities.chart.large ||
                reshareMessage.entities.chart.thumb) && (
                <div
                  className="stocktwits-reshare-chart"
                  onClick={(e) => {
                    if (onImageClick) {
                      e.stopPropagation();
                      const reshareChart = reshareMessage.entities.chart;
                      onImageClick(
                        reshareChart.original ||
                          reshareChart.large ||
                          reshareChart.default ||
                          reshareChart.thumb
                      );
                    }
                  }}
                  style={{ cursor: onImageClick ? "pointer" : "default" }}
                  title={onImageClick ? "Click to view full size" : undefined}
                >
                  <img
                    src={
                      reshareMessage.entities.chart.default ||
                      reshareMessage.entities.chart.original ||
                      reshareMessage.entities.chart.large ||
                      reshareMessage.entities.chart.thumb
                    }
                    alt="Chart"
                  />
                </div>
              )}
            {/* Reshared post symbols */}
            {reshareMessage.symbols && reshareMessage.symbols.length > 0 && (
              <div className="stocktwits-reshare-symbols">
                {reshareMessage.symbols.map((symbol) => (
                  <span key={symbol.id} className="stocktwits-reshare-symbol">
                    ${symbol.symbol}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sentiment badge */}
        {sentiment && (
          <div className="stocktwits-sentiment">
            <span
              className={`stocktwits-sentiment-badge stocktwits-sentiment-${sentiment.basic.toLowerCase()}`}
            >
              {sentiment.basic === "Bullish" ? "📈" : "📉"} {sentiment.basic}
            </span>
          </div>
        )}
      </div>

      {/* Footer: Stock symbols (fixed to bottom) */}
      {symbols.length > 0 && (
        <div className="stocktwits-symbols-footer">
          {symbols.map((symbol) => {
            const priceChange = intel.ticker_price_changes?.[symbol.symbol];
            const changePercent = priceChange?.change_percent;
            const currentPrice = priceChange?.current_price;
            const hasChange =
              changePercent !== undefined && changePercent !== null;

            return (
              <button
                key={symbol.id}
                className={`stocktwits-symbol-widget ${hasChange ? (changePercent >= 0 ? "positive" : "negative") : ""}`}
                onClick={() => onTickerClick && onTickerClick(symbol.symbol)}
                title={symbol.title}
              >
                <span className="symbol-text">{symbol.symbol}</span>
                {currentPrice != null && (
                  <span className="price-text">{formatCurrency(currentPrice)}</span>
                )}
                {hasChange && (
                  <span className="change-text">
                    {changePercent >= 0 ? "+" : ""}
                    {changePercent.toFixed(2)}%
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StockTwitsCard;
